import React, { useRef } from 'react';
import { Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import { useToast } from '@/hooks/use-toast';

interface HDExportButtonProps {
  mapARef: React.RefObject<any>;
  mapBRef: React.RefObject<any>;
  releaseA: string | number | null;
  releaseB: string | number | null;
  location?: string;
}

const HDExportButton: React.FC<HDExportButtonProps> = ({
  mapARef,
  mapBRef,
  releaseA,
  releaseB,
  location = 'Unknown Location'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { toast } = useToast();

  const exportHDComparison = async () => {
    if (!mapARef.current || !mapBRef.current || !releaseA || !releaseB) {
      toast({
        title: 'Cannot export comparison',
        description: 'Please ensure both maps are loaded and releases are selected',
        variant: 'destructive',
      });
      return;
    }

    // Show loading message
    toast({
      title: 'Capturing maps…',
      description: 'This may take a few seconds.',
    });

    try {
      // Use fixed dimensions for consistent export (1920x1080 for HD)
      const exportWidth = 1920;
      const exportHeight = 1080;
      const headerHeight = 100;
      const footerHeight = 50;
      const mapAreaHeight = exportHeight - headerHeight - footerHeight;
      
      // Create high-resolution canvas (2x for HD)
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext('2d')!;
      canvas.width = exportWidth * 2;
      canvas.height = exportHeight * 2;
      
      // Set canvas style for crisp rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.scale(2, 2);
      
      // Create a white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, exportWidth, exportHeight);
      
      // Add SYCEK branding header
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, exportWidth, headerHeight);
      
      // SYCEK logo and title
      ctx.fillStyle = '#00ff88';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('SYCEK OSINT', 30, 35);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 20px Arial';
      ctx.fillText('GEOINT Wayback Analysis', 30, 60);
      
      // Analysis details - properly aligned
      ctx.fillStyle = '#ffffff';
      ctx.font = '14px Arial';
      ctx.textAlign = 'right';
      const rightMargin = exportWidth - 30;
      ctx.fillText(`Location: ${location}`, rightMargin, 30);
      ctx.fillText(`Release A: ${releaseA}`, rightMargin, 50);
      ctx.fillText(`Release B: ${releaseB}`, rightMargin, 70);
      
      // Add timestamp
      const now = new Date();
      ctx.fillText(`Generated: ${now.toLocaleString()}`, rightMargin, 90);
      
      // Wait for maps to fully render all tiles
      console.log('Waiting for maps to render...');
      
      // Wait for both maps to be idle (all tiles loaded)
      const waitForMapIdle = (map: any): Promise<void> => {
        return new Promise((resolve) => {
          if (map && map.loaded()) {
            // Check if map is already idle
            map.once('idle', () => resolve());
            // Also set a timeout in case idle event doesn't fire
            setTimeout(() => resolve(), 3000);
          } else {
            // If map isn't loaded yet, wait a bit more
            setTimeout(() => resolve(), 2000);
          }
        });
      };
      
      await Promise.all([
        waitForMapIdle(mapARef.current),
        waitForMapIdle(mapBRef.current)
      ]);
      
      // Additional wait to ensure everything is rendered
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Calculate map dimensions for side-by-side display
      const mapWidth = exportWidth / 2;
      const mapY = headerHeight;
      const mapAHeight = mapAreaHeight;
      const mapBHeight = mapAreaHeight;
      
      // Try to get canvas directly from MapLibre GL maps first (most reliable)
      let mapAImage: HTMLImageElement | null = null;
      let mapBImage: HTMLImageElement | null = null;
      
      try {
        console.log('Attempting to get Map A canvas directly from MapLibre...');
        // Try to get the canvas directly from the map instance
        const mapACanvas = mapARef.current?.getCanvas();
        if (mapACanvas && mapACanvas.width > 0 && mapACanvas.height > 0) {
          console.log('Map A canvas found directly', { width: mapACanvas.width, height: mapACanvas.height });
          
          // For WebGL canvases, we need to read pixels and draw to a new canvas
          // because WebGL canvases are often tainted and can't be directly copied
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = mapACanvas.width;
          tempCanvas.height = mapACanvas.height;
          const tempCtx = tempCanvas.getContext('2d')!;
          
          // Try to draw the WebGL canvas - this may fail if tainted
          try {
            tempCtx.drawImage(mapACanvas, 0, 0);
            mapAImage = new Image();
            mapAImage.src = tempCanvas.toDataURL('image/png', 1.0);
          } catch (taintError) {
            console.warn('Canvas is tainted, trying alternative method...', taintError);
            // If tainted, try reading pixels from WebGL context
            const gl = mapACanvas.getContext('webgl') || mapACanvas.getContext('webgl2');
            if (gl) {
              const pixels = new Uint8Array(mapACanvas.width * mapACanvas.height * 4);
              gl.readPixels(0, 0, mapACanvas.width, mapACanvas.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
              
              // Flip vertically (WebGL has origin at bottom-left)
              const imageData = tempCtx.createImageData(mapACanvas.width, mapACanvas.height);
              for (let y = 0; y < mapACanvas.height; y++) {
                for (let x = 0; x < mapACanvas.width; x++) {
                  const srcIndex = ((mapACanvas.height - 1 - y) * mapACanvas.width + x) * 4;
                  const dstIndex = (y * mapACanvas.width + x) * 4;
                  imageData.data[dstIndex] = pixels[srcIndex];
                  imageData.data[dstIndex + 1] = pixels[srcIndex + 1];
                  imageData.data[dstIndex + 2] = pixels[srcIndex + 2];
                  imageData.data[dstIndex + 3] = pixels[srcIndex + 3];
                }
              }
              tempCtx.putImageData(imageData, 0, 0);
              mapAImage = new Image();
              mapAImage.src = tempCanvas.toDataURL('image/png', 1.0);
            } else {
              throw new Error('Could not get WebGL context');
            }
          }
          await new Promise<void>((resolve, reject) => {
            const onLoad = () => {
              console.log('Map A image loaded from canvas', { width: mapAImage!.width, height: mapAImage!.height });
              mapAImage!.removeEventListener('load', onLoad);
              mapAImage!.removeEventListener('error', onError);
              clearTimeout(timeoutId);
              resolve();
            };
            const onError = () => {
              mapAImage!.removeEventListener('load', onLoad);
              mapAImage!.removeEventListener('error', onError);
              clearTimeout(timeoutId);
              reject(new Error('Failed to load Map A image'));
            };
            mapAImage!.addEventListener('load', onLoad);
            mapAImage!.addEventListener('error', onError);
            const timeoutId = setTimeout(() => {
              mapAImage!.removeEventListener('load', onLoad);
              mapAImage!.removeEventListener('error', onError);
              reject(new Error('Map A image load timeout'));
            }, 10000);
          });
          console.log('Map A captured successfully from canvas');
        } else {
          throw new Error('Map A canvas not available or invalid');
        }
      } catch (e) {
        console.warn('Direct canvas capture failed for Map A, trying html2canvas...', e);
        // Fallback to html2canvas
        try {
          const mapAContainer = document.getElementById('map-left');
          if (mapAContainer) {
            const mapACanvas = await html2canvas(mapAContainer, {
              useCORS: true,
              allowTaint: true, // Allow taint for WebGL canvases
              backgroundColor: '#000000',
              scale: 2,
              logging: false,
              width: mapAContainer.offsetWidth,
              height: mapAContainer.offsetHeight
            });
            mapAImage = new Image();
            mapAImage.src = mapACanvas.toDataURL('image/png', 1.0);
            await new Promise<void>((resolve, reject) => {
              const onLoad = () => {
                mapAImage!.removeEventListener('load', onLoad);
                mapAImage!.removeEventListener('error', onError);
                clearTimeout(timeoutId);
                resolve();
              };
              const onError = () => {
                mapAImage!.removeEventListener('load', onLoad);
                mapAImage!.removeEventListener('error', onError);
                clearTimeout(timeoutId);
                reject(new Error('Failed to load Map A image'));
              };
              mapAImage!.addEventListener('load', onLoad);
              mapAImage!.addEventListener('error', onError);
              const timeoutId = setTimeout(() => {
                mapAImage!.removeEventListener('load', onLoad);
                mapAImage!.removeEventListener('error', onError);
                reject(new Error('Map A image load timeout'));
              }, 10000);
            });
            console.log('Map A captured successfully with html2canvas');
          }
        } catch (e2) {
          console.error('Both methods failed for Map A:', e2);
        }
      }
      
      try {
        console.log('Attempting to get Map B canvas directly from MapLibre...');
        // Try to get the canvas directly from the map instance
        const mapBCanvas = mapBRef.current?.getCanvas();
        if (mapBCanvas && mapBCanvas.width > 0 && mapBCanvas.height > 0) {
          console.log('Map B canvas found directly', { width: mapBCanvas.width, height: mapBCanvas.height });
          
          // For WebGL canvases, we need to read pixels and draw to a new canvas
          // because WebGL canvases are often tainted and can't be directly copied
          const tempCanvasB = document.createElement('canvas');
          tempCanvasB.width = mapBCanvas.width;
          tempCanvasB.height = mapBCanvas.height;
          const tempCtxB = tempCanvasB.getContext('2d')!;
          
          // Try to draw the WebGL canvas - this may fail if tainted
          try {
            tempCtxB.drawImage(mapBCanvas, 0, 0);
            mapBImage = new Image();
            mapBImage.src = tempCanvasB.toDataURL('image/png', 1.0);
          } catch (taintError) {
            console.warn('Canvas is tainted, trying alternative method...', taintError);
            // If tainted, try reading pixels from WebGL context
            const gl = mapBCanvas.getContext('webgl') || mapBCanvas.getContext('webgl2');
            if (gl) {
              const pixels = new Uint8Array(mapBCanvas.width * mapBCanvas.height * 4);
              gl.readPixels(0, 0, mapBCanvas.width, mapBCanvas.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
              
              // Flip vertically (WebGL has origin at bottom-left)
              const imageData = tempCtxB.createImageData(mapBCanvas.width, mapBCanvas.height);
              for (let y = 0; y < mapBCanvas.height; y++) {
                for (let x = 0; x < mapBCanvas.width; x++) {
                  const srcIndex = ((mapBCanvas.height - 1 - y) * mapBCanvas.width + x) * 4;
                  const dstIndex = (y * mapBCanvas.width + x) * 4;
                  imageData.data[dstIndex] = pixels[srcIndex];
                  imageData.data[dstIndex + 1] = pixels[srcIndex + 1];
                  imageData.data[dstIndex + 2] = pixels[srcIndex + 2];
                  imageData.data[dstIndex + 3] = pixels[srcIndex + 3];
                }
              }
              tempCtxB.putImageData(imageData, 0, 0);
              mapBImage = new Image();
              mapBImage.src = tempCanvasB.toDataURL('image/png', 1.0);
            } else {
              throw new Error('Could not get WebGL context');
            }
          }
          await new Promise<void>((resolve, reject) => {
            const onLoad = () => {
              console.log('Map B image loaded from canvas', { width: mapBImage!.width, height: mapBImage!.height });
              mapBImage!.removeEventListener('load', onLoad);
              mapBImage!.removeEventListener('error', onError);
              clearTimeout(timeoutId);
              resolve();
            };
            const onError = () => {
              mapBImage!.removeEventListener('load', onLoad);
              mapBImage!.removeEventListener('error', onError);
              clearTimeout(timeoutId);
              reject(new Error('Failed to load Map B image'));
            };
            mapBImage!.addEventListener('load', onLoad);
            mapBImage!.addEventListener('error', onError);
            const timeoutId = setTimeout(() => {
              mapBImage!.removeEventListener('load', onLoad);
              mapBImage!.removeEventListener('error', onError);
              reject(new Error('Map B image load timeout'));
            }, 10000);
          });
          console.log('Map B captured successfully from canvas');
        } else {
          throw new Error('Map B canvas not available or invalid');
        }
      } catch (e) {
        console.warn('Direct canvas capture failed for Map B, trying html2canvas...', e);
        // Fallback to html2canvas
        try {
          const mapBContainer = document.getElementById('map-right');
          if (mapBContainer) {
            const mapBCanvas = await html2canvas(mapBContainer, {
              useCORS: true,
              allowTaint: true, // Allow taint for WebGL canvases
              backgroundColor: '#000000',
              scale: 2,
              logging: false,
              width: mapBContainer.offsetWidth,
              height: mapBContainer.offsetHeight
            });
            mapBImage = new Image();
            mapBImage.src = mapBCanvas.toDataURL('image/png', 1.0);
            await new Promise<void>((resolve, reject) => {
              const onLoad = () => {
                mapBImage!.removeEventListener('load', onLoad);
                mapBImage!.removeEventListener('error', onError);
                clearTimeout(timeoutId);
                resolve();
              };
              const onError = () => {
                mapBImage!.removeEventListener('load', onLoad);
                mapBImage!.removeEventListener('error', onError);
                clearTimeout(timeoutId);
                reject(new Error('Failed to load Map B image'));
              };
              mapBImage!.addEventListener('load', onLoad);
              mapBImage!.addEventListener('error', onError);
              const timeoutId = setTimeout(() => {
                mapBImage!.removeEventListener('load', onLoad);
                mapBImage!.removeEventListener('error', onError);
                reject(new Error('Map B image load timeout'));
              }, 10000);
            });
            console.log('Map B captured successfully with html2canvas');
          }
        } catch (e2) {
          console.error('Both methods failed for Map B:', e2);
        }
      }
      
      if (mapAImage && mapBImage) {
        // Draw map A (left side) - scale to fit
        ctx.drawImage(mapAImage, 0, mapY, mapWidth, mapAHeight);
        
        // Add "Before" label
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, mapY, mapWidth, 35);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`BEFORE (${releaseA})`, mapWidth / 2, mapY + 25);
        
        // Draw map B (right side) - scale to fit
        ctx.drawImage(mapBImage, mapWidth, mapY, mapWidth, mapBHeight);
      
      // Add "After" label
        ctx.fillStyle = '#000000';
        ctx.fillRect(mapWidth, mapY, mapWidth, 35);
      ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`AFTER (${releaseB})`, (exportWidth * 3) / 4, mapY + 25);
      } else {
        // Fallback: Draw placeholder if maps aren't available
        ctx.fillStyle = '#cccccc';
        ctx.fillRect(0, headerHeight, exportWidth / 2, mapAreaHeight);
        ctx.fillRect(exportWidth / 2, headerHeight, exportWidth / 2, mapAreaHeight);
        
        ctx.fillStyle = '#666666';
        ctx.font = '16px Arial';
      ctx.textAlign = 'center';
        ctx.fillText(`Map A (${releaseA}) - Unable to capture`, exportWidth / 4, headerHeight + mapAreaHeight / 2);
        ctx.fillText(`Map B (${releaseB}) - Unable to capture`, (exportWidth * 3) / 4, headerHeight + mapAreaHeight / 2);
      }
      
      // Add footer with SYCEK branding - properly aligned
      const footerY = exportHeight - footerHeight;
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, footerY, exportWidth, footerHeight);
      
      ctx.fillStyle = '#00ff88';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('SYCEK OSINT - Advanced GEOINT Analysis', 30, footerY + 30);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';
      ctx.fillText('Powered by SYCEK Intelligence Platform', rightMargin, footerY + 30);
      
      // Download the image
      const link = document.createElement('a');
      link.download = `sycek-geoint-comparison-${releaseA}-${releaseB}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      
    } catch (error) {
      console.error('Error exporting HD comparison:', error);
      toast({
        title: 'Export failed',
        description: `Failed to export comparison: ${error instanceof Error ? error.message : 'Unknown error'}. Please ensure both maps are fully loaded.`,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col space-y-2">
      <button
        onClick={exportHDComparison}
        disabled={!mapARef.current || !mapBRef.current || !releaseA || !releaseB}
        className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-500 text-white rounded-md text-sm font-medium transition-colors"
      >
        <Download className="w-4 h-4" />
        <span>Export HD Comparison</span>
      </button>
      
      <div className="text-xs text-muted-foreground text-center">
        Exports high-resolution comparison with SYCEK branding
      </div>
      
      {/* Hidden canvas for rendering */}
      <canvas 
        ref={canvasRef} 
        style={{ display: 'none' }}
        width={2000} 
        height={1000}
      />
    </div>
  );
};

export default HDExportButton;
